import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useBusinessContext } from "@/providers/business-provider";
import { useToast } from "@/hooks/use-toast";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Users, 
  BarChart3, 
  PieChart,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  Zap,
  Target,
  Activity,
  RefreshCw
} from "lucide-react";
import { format } from "date-fns";
import { apiRequest } from "@/lib/queryClient";
import { useQuery } from "@tanstack/react-query";

interface DashboardMetrics {
  revenue: {
    current: number;
    previous: number;
    change: number;
  };
  expenses: {
    current: number;
    previous: number;
    change: number;
  };
  profit: {
    current: number;
    previous: number;
    change: number;
  };
  cashFlow: {
    current: number;
    previous: number;
    change: number;
  };
  customers: {
    total: number;
    active: number;
    new: number;
  };
  transactions: {
    total: number;
    pending: number;
    processed: number;
  };
}

export function InteractiveDashboard() {
  const { activeBusiness } = useBusinessContext();
  const { toast } = useToast();
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  // Fetch real-time dashboard metrics using actual business data
  const { data: metrics, isLoading, refetch } = useQuery<DashboardMetrics>({
    queryKey: [`/api/businesses/${activeBusiness?.id}/dashboard/metrics`, timeRange],
    enabled: !!activeBusiness?.id,
    refetchInterval: 30000, // Auto-refresh every 30 seconds for real-time data
    staleTime: 0,
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refetch();
      toast({
        title: "Dashboard Refreshed",
        description: "All metrics updated with latest business data",
      });
    } catch (error) {
      toast({
        title: "Refresh Failed",
        description: "Failed to update dashboard metrics",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const getChangeIcon = (change: number) => {
    return change >= 0 ? TrendingUp : TrendingDown;
  };

  const getChangeColor = (change: number) => {
    return change >= 0 ? 'text-green-600' : 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header with real-time controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            {activeBusiness?.name} Dashboard
          </h2>
          <p className="text-muted-foreground mt-1">
            Real-time business insights and analytics
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Button
              variant={timeRange === '7d' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTimeRange('7d')}
            >
              7 Days
            </Button>
            <Button
              variant={timeRange === '30d' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTimeRange('30d')}
            >
              30 Days
            </Button>
            <Button
              variant={timeRange === '90d' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTimeRange('90d')}
            >
              90 Days
            </Button>
          </div>
          
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards with Real Data */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(metrics?.revenue.current || 0)}
                </p>
                <div className="flex items-center gap-1 mt-1">
                  {(() => {
                    const Icon = getChangeIcon(metrics?.revenue.change || 0);
                    return <Icon className={`h-4 w-4 ${getChangeColor(metrics?.revenue.change || 0)}`} />;
                  })()}
                  <span className={`text-sm font-medium ${getChangeColor(metrics?.revenue.change || 0)}`}>
                    {formatPercentage(metrics?.revenue.change || 0)}
                  </span>
                  <span className="text-sm text-muted-foreground">vs last period</span>
                </div>
              </div>
              <div className="h-12 w-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Expenses</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(metrics?.expenses.current || 0)}
                </p>
                <div className="flex items-center gap-1 mt-1">
                  {(() => {
                    const Icon = getChangeIcon(metrics?.expenses.change || 0);
                    return <Icon className={`h-4 w-4 ${getChangeColor(metrics?.expenses.change || 0)}`} />;
                  })()}
                  <span className={`text-sm font-medium ${getChangeColor(metrics?.expenses.change || 0)}`}>
                    {formatPercentage(metrics?.expenses.change || 0)}
                  </span>
                  <span className="text-sm text-muted-foreground">vs last period</span>
                </div>
              </div>
              <div className="h-12 w-12 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
                <TrendingDown className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Net Profit</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(metrics?.profit.current || 0)}
                </p>
                <div className="flex items-center gap-1 mt-1">
                  {(() => {
                    const Icon = getChangeIcon(metrics?.profit.change || 0);
                    return <Icon className={`h-4 w-4 ${getChangeColor(metrics?.profit.change || 0)}`} />;
                  })()}
                  <span className={`text-sm font-medium ${getChangeColor(metrics?.profit.change || 0)}`}>
                    {formatPercentage(metrics?.profit.change || 0)}
                  </span>
                  <span className="text-sm text-muted-foreground">vs last period</span>
                </div>
              </div>
              <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <Target className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Cash Flow</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(metrics?.cashFlow.current || 0)}
                </p>
                <div className="flex items-center gap-1 mt-1">
                  {(() => {
                    const Icon = getChangeIcon(metrics?.cashFlow.change || 0);
                    return <Icon className={`h-4 w-4 ${getChangeColor(metrics?.cashFlow.change || 0)}`} />;
                  })()}
                  <span className={`text-sm font-medium ${getChangeColor(metrics?.cashFlow.change || 0)}`}>
                    {formatPercentage(metrics?.cashFlow.change || 0)}
                  </span>
                  <span className="text-sm text-muted-foreground">vs last period</span>
                </div>
              </div>
              <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                <Activity className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-yellow-600" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button
              onClick={() => window.location.href = '/transactions/create'}
              variant="outline"
              className="h-auto p-4 flex flex-col items-center gap-3 hover:shadow-md transition-all"
            >
              <div className="h-10 w-10 bg-green-500 hover:bg-green-600 rounded-lg flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <div className="text-center">
                <p className="font-semibold">Add Transaction</p>
                <p className="text-xs text-muted-foreground">Record new income or expense</p>
              </div>
            </Button>

            <Button
              onClick={() => window.location.href = '/reports'}
              variant="outline"
              className="h-auto p-4 flex flex-col items-center gap-3 hover:shadow-md transition-all"
            >
              <div className="h-10 w-10 bg-blue-500 hover:bg-blue-600 rounded-lg flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-white" />
              </div>
              <div className="text-center">
                <p className="font-semibold">Generate Report</p>
                <p className="text-xs text-muted-foreground">Create financial reports</p>
              </div>
            </Button>

            <Button
              onClick={() => window.location.href = '/customers'}
              variant="outline"
              className="h-auto p-4 flex flex-col items-center gap-3 hover:shadow-md transition-all"
            >
              <div className="h-10 w-10 bg-purple-500 hover:bg-purple-600 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div className="text-center">
                <p className="font-semibold">Manage Customers</p>
                <p className="text-xs text-muted-foreground">View and manage clients</p>
              </div>
            </Button>

            <Button
              onClick={() => window.location.href = '/cash-flow/projections'}
              variant="outline"
              className="h-auto p-4 flex flex-col items-center gap-3 hover:shadow-md transition-all"
            >
              <div className="h-10 w-10 bg-orange-500 hover:bg-orange-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <div className="text-center">
                <p className="font-semibold">Cash Flow Analysis</p>
                <p className="text-xs text-muted-foreground">View cash flow projections</p>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Business Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Customer Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total Customers</span>
                <span className="font-semibold">{metrics?.customers.total || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Active Customers</span>
                <span className="font-semibold text-green-600">{metrics?.customers.active || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">New This Month</span>
                <Badge variant="secondary">+{metrics?.customers.new || 0}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-green-600" />
              Transaction Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total Transactions</span>
                <span className="font-semibold">{metrics?.transactions.total || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Processed</span>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="font-semibold text-green-600">{metrics?.transactions.processed || 0}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Pending Review</span>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-yellow-600" />
                  <Badge variant="outline" className="text-yellow-600">{metrics?.transactions.pending || 0}</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}